/**
 * PDF Library — local storage (IndexedDB + localStorage)
 * API shape matches a future Supabase layer ({ data, error }).
 *
 * Markup lives in partials/library.html and is fetched on FIRST navigation
 * (AGENTS.md SKILL 5). index.html holds only an empty <section id="library">.
 *
 * ES module. Loaded via <script type="module">, so it is deferred and evaluates
 * after every classic script. Two consequences worth knowing before editing:
 *
 *  1. THE BOOT-TIME readyState BRANCH IS GONE, and its removal is the whole point
 *     of the lazy load. initLibraryUI() binds listeners with addEventListener;
 *     with the markup absent at boot it would bind nothing, and because refresh()
 *     only re-renders, the screen would have been permanently inert with no error
 *     anywhere. Binding now happens once, after the partial is injected. The
 *     null-guards inside initLibraryUI are what made that failure silent rather
 *     than loud, so they are no longer load-bearing — do not rely on them.
 *  2. It must stay ordered AFTER subjects.js among the module scripts, because
 *     renderSidebar() reads window.Subjects. Modules execute in document order.
 *
 * Exposes window.PDFLibrary (transition shim, AGENTS.md SKILL 1) — which is load
 * bearing: index.html uses onclick="PDFLibrary.cancelUploadModal()", and an inline
 * handler attribute resolves against the global object and cannot see module scope.
 */
import { ensurePdfJs } from './cdn-libs.js';

const PDFLibrary = (() => {
    const DB_NAME = 'studymeneer_pdf_library';
    const STORE = 'pdf_files';
    const META_KEY = 'pdf_library_meta';
    const STORAGE_LIMIT_BYTES = 500 * 1024 * 1024;

    const CATEGORY_LABELS = {
        textbook: 'Textbook',
        past_paper: 'Past paper',
        notes: 'Notes',
        slides: 'Slides',
        other: 'Other',
    };

    function openDb() {
        return new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, 1);
            req.onerror = () => reject(req.error);
            req.onsuccess = () => resolve(req.result);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE);
                }
            };
        });
    }

    function idbPut(id, arrayBuffer) {
        return openDb().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE, 'readwrite');
                    tx.oncomplete = () => resolve();
                    tx.onerror = () => reject(tx.error);
                    tx.objectStore(STORE).put(arrayBuffer, id);
                })
        );
    }

    function idbGet(id) {
        return openDb().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE, 'readonly');
                    const r = tx.objectStore(STORE).get(id);
                    r.onerror = () => reject(r.error);
                    r.onsuccess = () => resolve(r.result);
                })
        );
    }

    function idbDelete(id) {
        return openDb().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE, 'readwrite');
                    tx.oncomplete = () => resolve();
                    tx.onerror = () => reject(tx.error);
                    tx.objectStore(STORE).delete(id);
                })
        );
    }

    function _readMeta() {
        try {
            const raw = localStorage.getItem(META_KEY);
            const arr = JSON.parse(raw || '[]');
            return Array.isArray(arr) ? arr : [];
        } catch (_) {
            return [];
        }
    }

    function _writeMeta(records) {
        localStorage.setItem(META_KEY, JSON.stringify(records));
    }

    function _newId() {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
        return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }

    function _totalBytesSync() {
        return _readMeta().reduce((s, r) => s + (Number(r.sizeBytes) || 0), 0);
    }

    function getFiles(subjectId, category) {
        try {
            let list = _readMeta();
            if (subjectId != null && subjectId !== '') {
                list = list.filter((r) => r.subjectId === subjectId);
            }
            if (category != null && category !== '') {
                list = list.filter((r) => r.category === category);
            }
            list.sort((a, b) => {
                const ta = new Date(a.createdAt || 0).getTime();
                const tb = new Date(b.createdAt || 0).getTime();
                return tb - ta;
            });
            return { data: list, error: null };
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to read library' } };
        }
    }

    function getRecentFiles(limit) {
        const lim = limit == null ? 8 : Math.max(0, parseInt(limit, 10) || 8);
        try {
            const list = _readMeta()
                .filter((r) => r.lastOpenedAt)
                .sort((a, b) => {
                    const ta = new Date(a.lastOpenedAt).getTime();
                    const tb = new Date(b.lastOpenedAt).getTime();
                    return tb - ta;
                })
                .slice(0, lim);
            return { data: list, error: null };
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to read recent files' } };
        }
    }

    async function uploadFile(file, subjectId, category, customName) {
        if (!file || file.type !== 'application/pdf') {
            return { data: null, error: { message: 'Please choose a PDF file.' } };
        }
        if (!subjectId) {
            return { data: null, error: { message: 'Please select a subject.' } };
        }
        if (!category || !CATEGORY_LABELS[category]) {
            return { data: null, error: { message: 'Please select a category.' } };
        }

        const addBytes = file.size || 0;
        if (_totalBytesSync() + addBytes > STORAGE_LIMIT_BYTES) {
            return { data: null, error: { message: 'Upload would exceed the 500 MB library limit.' } };
        }

        // Only reached when a student is actually uploading a PDF, which is the
        // only thing on this screen that needs the engine.
        const pdfjsLib = await ensurePdfJs();
        if (!pdfjsLib) {
            return { data: null, error: { message: 'PDF engine not loaded.' } };
        }

        let arrayBuffer;
        try {
            arrayBuffer = await file.arrayBuffer();
        } catch (e) {
            return { data: null, error: { message: 'Could not read the file.' } };
        }

        let pageCount = 0;
        try {
            const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer.slice(0) });
            const tmpDoc = await loadingTask.promise;
            pageCount = tmpDoc.numPages;
            try {
                if (typeof tmpDoc.destroy === 'function') await tmpDoc.destroy();
            } catch (_) { /* ignore */ }
        } catch (e) {
            return { data: null, error: { message: 'Could not read this PDF (corrupt or encrypted).' } };
        }

        const id = _newId();
        const now = new Date().toISOString();
        const name = (customName || file.name || 'document').replace(/\.pdf$/i, '').trim() || 'document';

        try {
            await idbPut(id, arrayBuffer);
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to store PDF.' } };
        }

        const record = {
            id,
            name,
            subjectId,
            category,
            sizeBytes: addBytes,
            pageCount,
            lastOpenedAt: null,
            lastPage: 1,
            createdAt: now,
        };

        const meta = _readMeta();
        meta.push(record);
        _writeMeta(meta);

        return { data: record, error: null };
    }

    async function openFile(fileId) {
        const meta = _readMeta();
        const idx = meta.findIndex((r) => r.id === fileId);
        if (idx === -1) {
            return { data: null, error: { message: 'File not found.' } };
        }

        let buffer;
        try {
            buffer = await idbGet(fileId);
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to read file data.' } };
        }
        if (!buffer) {
            return { data: null, error: { message: 'PDF data missing from storage.' } };
        }

        const now = new Date().toISOString();
        meta[idx] = { ...meta[idx], lastOpenedAt: now };
        _writeMeta(meta);

        const blob = new Blob([buffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        return {
            data: {
                url,
                meta: meta[idx],
            },
            error: null,
        };
    }

    function saveLastPage(fileId, pageNumber) {
        try {
            const meta = _readMeta();
            const idx = meta.findIndex((r) => r.id === fileId);
            if (idx === -1) return { data: null, error: { message: 'File not found.' } };
            const n = parseInt(pageNumber, 10);
            if (isNaN(n) || n < 1) return { data: meta[idx], error: null };
            meta[idx] = { ...meta[idx], lastPage: n };
            _writeMeta(meta);
            return { data: meta[idx], error: null };
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to save page.' } };
        }
    }

    async function deleteFile(fileId) {
        try {
            await idbDelete(fileId);
        } catch (e) {
            return { data: null, error: { message: e.message || 'Failed to delete file data.' } };
        }
        const next = _readMeta().filter((r) => r.id !== fileId);
        _writeMeta(next);
        return { data: true, error: null };
    }

    function getTotalStorageBytes() {
        try {
            return { data: _totalBytesSync(), error: null };
        } catch (e) {
            return { data: null, error: { message: e.message } };
        }
    }

    function categoryLabel(slug) {
        return CATEGORY_LABELS[slug] || slug;
    }

    // ── UI (Library tab) ─────────────────────────────────────────────────────

    let _uiState = {
        mode: 'browse',
        subjectId: null,
        category: null,
    };
    let _pendingUploadFile = null;

    function _fmtBytes(n) {
        if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
        if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${n} B`;
    }

    function _fmtDate(iso) {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        } catch (_) {
            return '—';
        }
    }

    function _fileCountForSubject(sid) {
        return _readMeta().filter((r) => r.subjectId === sid).length;
    }

    /**
     * Re-point this library's files at another subject.
     *
     * Exposed so merging two subjects does not require anyone to reach into
     * pdf_library_meta from outside — the same rule that made flashcards.js
     * reading the Subjects key a recorded defect. Metadata only: the PDF bytes
     * live in IndexedDB and are not touched.
     */
    function reassignSubject(fromId, toId) {
        const meta = _readMeta();
        let moved = 0;
        const next = meta.map((r) => {
            if (r && String(r.subjectId) === String(fromId)) {
                moved++;
                return { ...r, subjectId: toId };
            }
            return r;
        });
        if (moved > 0) _writeMeta(next);
        return moved;
    }

    function _totalFileCount() {
        return _readMeta().length;
    }

    function renderSidebar() {
        const subjects = window.Subjects ? window.Subjects.getAll() : [];
        const allCount = _totalFileCount();
        const recentEl = document.getElementById('libraryNavRecent');
        const allEl = document.getElementById('libraryNavAll');
        const subNav = document.getElementById('librarySubjectNav');
        if (!recentEl || !allEl || !subNav) return;

        allEl.querySelector('.library-nav-badge').textContent = String(allCount);

        const recentN = _readMeta().filter((r) => r.lastOpenedAt).length;
        recentEl.querySelector('.library-nav-badge').textContent = String(recentN);

        subNav.innerHTML = subjects
            .map((s) => {
                const c = _fileCountForSubject(s.id);
                const active = _uiState.mode === 'browse' && _uiState.subjectId === s.id;
                return `
                <button type="button" class="library-subject-nav-item${active ? ' active' : ''}"
                    data-subject-id="${escapeHtml(s.id)}">
                    <span class="library-subject-dot" style="background:${escapeHtml(s.colour)}"></span>
                    <span class="library-subject-nav-label">${escapeHtml(s.name)}</span>
                    <span class="library-nav-badge">${c}</span>
                </button>`;
            })
            .join('');

        subNav.querySelectorAll('.library-subject-nav-item').forEach((btn) => {
            btn.addEventListener('click', () => {
                _uiState = { mode: 'browse', subjectId: btn.dataset.subjectId, category: _uiState.category };
                _syncSidebarActive();
                renderMain();
            });
        });
    }

    function _syncSidebarActive() {
        const allEl = document.getElementById('libraryNavAll');
        const recentEl = document.getElementById('libraryNavRecent');
        if (allEl) allEl.classList.toggle('active', _uiState.mode === 'browse' && _uiState.subjectId == null);
        if (recentEl) recentEl.classList.toggle('active', _uiState.mode === 'recent');

        document.querySelectorAll('.library-subject-nav-item').forEach((btn) => {
            const sid = btn.dataset.subjectId;
            btn.classList.toggle('active', _uiState.mode === 'browse' && _uiState.subjectId === sid);
        });
    }

    function _setCategoryTab(cat) {
        _uiState.category = cat;
        document.querySelectorAll('#libraryCategoryBar .category-filter-btn').forEach((b) => {
            const c = b.dataset.category || '';
            const isAll = !c;
            b.classList.toggle('active', isAll ? cat == null || cat === '' : cat === c);
        });
        renderMain();
    }

    function renderMain() {
        const breadcrumb = document.getElementById('libraryBreadcrumb');
        const headerTitle = document.getElementById('libraryHeaderTitle');
        const headerStats = document.getElementById('libraryHeaderStats');
        const grid = document.getElementById('libraryFileGrid');
        const recentList = document.getElementById('libraryRecentList');
        const browseWrap = document.getElementById('libraryBrowsePanel');
        const recentWrap = document.getElementById('libraryRecentPanel');
        const catBar = document.getElementById('libraryCategoryBar');

        if (!grid || !breadcrumb) return;

        if (_uiState.mode === 'recent') {
            if (browseWrap) browseWrap.style.display = 'none';
            if (recentWrap) recentWrap.style.display = '';
            if (catBar) catBar.style.display = 'none';

            breadcrumb.textContent = 'Library › Recently opened';
            if (headerTitle) headerTitle.textContent = 'Recently opened';
            if (headerStats) headerStats.textContent = '';

            const res = getRecentFiles(50);
            const rows = res.data || [];
            if (!recentList) return;

            if (rows.length === 0) {
                recentList.innerHTML = '<p class="library-empty-hint">No files opened yet. Open a PDF from the library to see it here.</p>';
            } else {
                recentList.innerHTML = rows
                    .map((r) => {
                        const sub = window.Subjects ? window.Subjects.getById(r.subjectId) : null;
                        const subName = sub ? sub.name : 'Unknown subject';
                        const subColour = sub ? sub.colour : 'var(--text-muted)';
                        const cat = categoryLabel(r.category);
                        return `
                        <div class="library-recent-row" data-file-id="${escapeHtml(r.id)}">
                            <span class="library-recent-type" title="PDF">📕</span>
                            <div class="library-recent-main">
                                <div class="library-recent-name">${escapeHtml(r.name)}</div>
                                <div class="library-recent-meta">
                                    <span class="library-subject-pill"><span class="library-subject-dot" style="background:${escapeHtml(subColour)}"></span>${escapeHtml(subName)}</span>
                                    <span class="library-recent-cat">${escapeHtml(cat)}</span>
                                </div>
                            </div>
                            <span class="library-recent-time">${escapeHtml(_fmtDate(r.lastOpenedAt))}</span>
                            <span class="library-recent-page">Page ${r.lastPage != null ? r.lastPage : 1}</span>
                            <button type="button" class="btn btn-sm btn-primary library-open-btn" data-file-id="${escapeHtml(r.id)}">Open</button>
                        </div>`;
                    })
                    .join('');

                recentList.querySelectorAll('.library-open-btn').forEach((btn) => {
                    btn.addEventListener('click', () => openFileInReader(btn.dataset.fileId));
                });
            }
            _updateStorageBar();
            _syncSidebarActive();
            return;
        }

        if (browseWrap) browseWrap.style.display = '';
        if (recentWrap) recentWrap.style.display = 'none';
        if (catBar) catBar.style.display = '';

        const subjects = window.Subjects ? window.Subjects.getAll() : [];
        let title = 'All files';
        let crumb = 'Library › All files';
        if (_uiState.subjectId) {
            const s = window.Subjects ? window.Subjects.getById(_uiState.subjectId) : null;
            title = s ? s.name : 'Subject';
            crumb = `Library › ${s ? s.name : 'Subject'}`;
        }
        breadcrumb.textContent = crumb;
        if (headerTitle) headerTitle.textContent = title;

        const catFilter = _uiState.category || null;
        const filesRes =
            _uiState.subjectId != null
                ? getFiles(_uiState.subjectId, catFilter)
                : getFiles(null, catFilter);
        const files = filesRes.data || [];

        const totalPagesSum = files.reduce((s, f) => s + (Number(f.pageCount) || 0), 0);
        if (headerStats) {
            headerStats.textContent = `${files.length} file${files.length !== 1 ? 's' : ''} · ${totalPagesSum} page${totalPagesSum !== 1 ? 's' : ''}`;
        }

        const cards = files
            .map((f) => {
                const cat = categoryLabel(f.category);
                return `
            <div class="library-file-card section-card" data-file-id="${escapeHtml(f.id)}">
                <div class="library-file-card-top">
                    <span class="badge badge-secondary library-cat-badge">${escapeHtml(cat)}</span>
                </div>
                <div class="library-file-name" title="${escapeHtml(f.name)}">${escapeHtml(f.name)}</div>
                <div class="library-file-meta">
                    <span>${f.pageCount != null ? f.pageCount : '?'} p.</span>
                    <span>·</span>
                    <span>${escapeHtml(_fmtBytes(f.sizeBytes || 0))}</span>
                </div>
                <div class="library-file-opened">Opened: ${escapeHtml(_fmtDate(f.lastOpenedAt))}</div>
                <div class="library-file-actions">
                    <button type="button" class="btn btn-sm btn-primary library-card-open" data-file-id="${escapeHtml(f.id)}">Open</button>
                    <button type="button" class="btn btn-sm btn-danger library-card-delete" data-file-id="${escapeHtml(f.id)}">Delete</button>
                </div>
            </div>`;
            })
            .join('');

        grid.innerHTML =
            cards +
            `<div class="library-upload-slot section-card" id="libraryDropSlot">
                <span class="library-upload-icon">📤</span>
                <p class="library-upload-title">Drop PDF here or click to upload</p>
                <button type="button" class="btn btn-secondary btn-sm" id="libraryUploadBtn">Choose file</button>
                <input type="file" id="libraryFileInput" accept=".pdf,application/pdf" style="display:none" />
            </div>`;

        grid.querySelectorAll('.library-card-open').forEach((btn) => {
            btn.addEventListener('click', () => openFileInReader(btn.dataset.fileId));
        });
        grid.querySelectorAll('.library-card-delete').forEach((btn) => {
            btn.addEventListener('click', () => confirmDelete(btn.dataset.fileId));
        });

        _wireUploadZone(document.getElementById('libraryDropSlot'));

        document.querySelectorAll('#libraryCategoryBar .category-filter-btn').forEach((b) => {
            const c = b.dataset.category || '';
            const isAllBtn = !c;
            const active =
                _uiState.category == null || _uiState.category === ''
                    ? isAllBtn
                    : _uiState.category === c;
            b.classList.toggle('active', active);
        });

        _updateStorageBar();
        _syncSidebarActive();
    }

    function _updateStorageBar() {
        const wrap = document.getElementById('libraryStorageBar');
        const fill = document.getElementById('libraryStorageFill');
        const label = document.getElementById('libraryStorageLabel');
        if (!wrap || !fill || !label) return;

        const total = _totalBytesSync();
        const max = STORAGE_LIMIT_BYTES;
        const pct = Math.min(100, (total / max) * 100);
        fill.style.width = `${pct}%`;
        const mbUsed = (total / (1024 * 1024)).toFixed(2);
        const mbMax = (max / (1024 * 1024)).toFixed(0);
        label.textContent = `${mbUsed} MB of ${mbMax} MB used`;
        wrap.classList.toggle('library-storage-bar--warning', pct >= 80);
    }

    function _wireUploadZone(slot) {
        if (!slot) return;
        const input = document.getElementById('libraryFileInput');
        const btn = document.getElementById('libraryUploadBtn');

        const pick = () => input && input.click();

        slot.addEventListener('dragover', (e) => {
            e.preventDefault();
            slot.classList.add('dragover');
        });
        slot.addEventListener('dragleave', () => slot.classList.remove('dragover'));
        slot.addEventListener('drop', (e) => {
            e.preventDefault();
            slot.classList.remove('dragover');
            const f = e.dataTransfer.files[0];
            if (f && f.type === 'application/pdf') {
                _pendingUploadFile = f;
                openUploadModal();
            } else if (typeof showNotification === 'function') {
                showNotification('Please drop a PDF file.', 'error');
            }
        });
        slot.addEventListener('click', (e) => {
            if (e.target.closest('button')) return;
            pick();
        });
        if (btn) btn.addEventListener('click', (e) => {
            e.stopPropagation();
            pick();
        });
        if (input) {
            input.onchange = () => {
                const f = input.files[0];
                if (f) {
                    _pendingUploadFile = f;
                    openUploadModal();
                }
                input.value = '';
            };
        }
    }

    async function openUploadModal() {
        if (!await window.Overlays?.ensure('library')) return;
        const modal = document.getElementById('pdfLibraryUploadModal');
        const nameInp = document.getElementById('pdfLibraryUploadName');
        const subSel = document.getElementById('pdfLibraryUploadSubject');
        const errEl = document.getElementById('pdfLibraryUploadError');
        const saveBtn = document.getElementById('pdfLibraryUploadSave');
        if (!modal || !nameInp || !subSel) return;

        const f = _pendingUploadFile;
        if (!f) return;

        errEl.style.display = 'none';
        errEl.textContent = '';
        nameInp.value = f.name.replace(/\.pdf$/i, '');

        const subjects = window.Subjects ? window.Subjects.getAll() : [];
        subSel.innerHTML =
            '<option value="">— Select subject —</option>' +
            subjects.map((s) => `<option value="${escapeHtml(s.id)}">${escapeHtml(s.name)}</option>`).join('');

        if (_uiState.subjectId && subjects.some((s) => s.id === _uiState.subjectId)) {
            subSel.value = _uiState.subjectId;
        }

        document.querySelectorAll('input[name="pdfLibraryCategory"]').forEach((r) => {
            r.checked = false;
        });

        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save to library';
        }

        modal.style.display = 'block';
    }

    function closeUploadModal() {
        const modal = document.getElementById('pdfLibraryUploadModal');
        if (modal) modal.style.display = 'none';
        _pendingUploadFile = null;
    }

    async function submitUpload() {
        const errEl = document.getElementById('pdfLibraryUploadError');
        const nameInp = document.getElementById('pdfLibraryUploadName');
        const subSel = document.getElementById('pdfLibraryUploadSubject');
        const saveBtn = document.getElementById('pdfLibraryUploadSave');
        const f = _pendingUploadFile;

        errEl.style.display = 'none';
        errEl.textContent = '';

        if (!f) {
            errEl.textContent = 'No file selected.';
            errEl.style.display = '';
            return;
        }

        const customName = nameInp ? nameInp.value.trim() : '';
        const subjectId = subSel ? subSel.value : '';
        const catRadio = document.querySelector('input[name="pdfLibraryCategory"]:checked');
        const category = catRadio ? catRadio.value : '';

        if (!category) {
            errEl.textContent = 'Please select a category.';
            errEl.style.display = '';
            return;
        }

        if (!subjectId) {
            errEl.textContent = 'Please select a subject.';
            errEl.style.display = '';
            return;
        }

        const total = _totalBytesSync();
        if (total + (f.size || 0) > STORAGE_LIMIT_BYTES) {
            errEl.textContent = 'This upload would exceed the 500 MB library limit.';
            errEl.style.display = '';
            return;
        }

        if (saveBtn) {
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving…';
        }

        const res = await uploadFile(f, subjectId, category, customName);

        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save to library';
        }

        if (res.error) {
            errEl.textContent = res.error.message || 'Upload failed.';
            errEl.style.display = '';
            return;
        }

        closeUploadModal();
        renderSidebar();
        renderMain();
        if (typeof showNotification === 'function') {
            showNotification(`Saved “${res.data.name}” to your library`, 'success');
        }
    }

    async function openFileInReader(fileId) {
        const res = await openFile(fileId);
        if (res.error) {
            if (typeof showNotification === 'function') showNotification(res.error.message, 'error');
            return;
        }
        const { url, meta } = res.data;
        const initialPage = meta.lastPage != null ? meta.lastPage : 1;

        if (typeof showSection === 'function') showSection('pdf-reader');

        if (window.PdfReader && typeof window.PdfReader.loadFromBlobUrl === 'function') {
            await window.PdfReader.loadFromBlobUrl(url, `${meta.name}.pdf`, {
                libraryFileId: fileId,
                initialPage,
            });
        } else {
            if (typeof showNotification === 'function') {
                showNotification('PDF reader is not ready.', 'error');
            }
            URL.revokeObjectURL(url);
        }

        if (typeof showNotification === 'function') {
            showNotification(`Opened “${meta.name}”`, 'success');
        }
    }

    function confirmDelete(fileId) {
        if (!confirm('Remove this PDF from the library?')) return;
        deleteFile(fileId).then((res) => {
            if (res.error) {
                if (typeof showNotification === 'function') showNotification(res.error.message, 'error');
                return;
            }
            renderSidebar();
            renderMain();
            if (typeof showNotification === 'function') showNotification('File removed from library', 'info');
        });
    }

    function escapeHtml(str) {
        if (typeof window.escapeHtml === 'function') return window.escapeHtml(str);
        if (!str) return '';
        const div = document.createElement('div');
        div.appendChild(document.createTextNode(String(str)));
        return div.innerHTML;
    }

    function initLibraryUI() {
        const allBtn = document.getElementById('libraryNavAll');
        const recentBtn = document.getElementById('libraryNavRecent');
        const addSub = document.getElementById('libraryAddSubjectBtn');
        const catBar = document.getElementById('libraryCategoryBar');

        if (allBtn) {
            allBtn.addEventListener('click', () => {
                _uiState = { mode: 'browse', subjectId: null, category: _uiState.category };
                _syncSidebarActive();
                renderMain();
            });
        }
        if (recentBtn) {
            recentBtn.addEventListener('click', () => {
                _uiState = { mode: 'recent', subjectId: null, category: null };
                _syncSidebarActive();
                renderMain();
            });
        }
        if (addSub) {
            addSub.addEventListener('click', () => {
                window._lazyCall('subjectHub', 'openSubjectModal');
            });
        }
        if (catBar) {
            catBar.querySelectorAll('.category-filter-btn').forEach((b) => {
                b.addEventListener('click', () => {
                    const c = b.dataset.category;
                    _setCategoryTab(c || null);
                });
            });
        }

        // Save is `lib-save-upload` in LIB_OVERLAY_CLICK_ACTIONS. Binding it by
        // id here worked only because this runs after the overlay is injected —
        // one ordering change away from the silent-modal failure, and invisible
        // to the guard that reads data-actions.

        renderSidebar();
        renderMain();
    }

    const PARTIAL_URL = 'partials/library.html';
    let _partialLoaded = false;
    let _uiBound = false;

    /** Fetches and injects partials/library.html once. Safe on every call. */
    async function ensureLoaded() {
        if (_partialLoaded) return true;

        const host = document.getElementById('library');
        if (!host) {
            console.error('[PDFLibrary] section #library is missing from index.html');
            return false;
        }

        try {
            const resp = await fetch(PARTIAL_URL);
            if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
            host.innerHTML = await resp.text();
        window.Icons?.hydrate?.(host);
        } catch (err) {
            console.error('[PDFLibrary] could not load partial:', err);
            host.textContent = '';
            const msg = document.createElement('p');
            msg.style.padding = '24px';
            msg.style.opacity = '.8';
            msg.textContent = 'The library could not load. If you are offline, reconnect once and it will work from then on.';
            host.appendChild(msg);
            return false;
        }

        _partialLoaded = true;
        _bindLibraryActions();
        return true;
    }

    // ── Delegated event handling (ROADMAP_HANDOVER §2.1) ──────────────────────

    /**
     * One action, and it is worth saying why the map exists for it: the rest of
     * this screen was already bound with addEventListener in initLibraryUI(),
     * and the file rows are built with createElement rather than template
     * literals. That is why pdfLibrary.js generates no inline handlers at all
     * while most modules generate a dozen — it is the shape the conversion is
     * moving everything else toward.
     */
    const LIB_CLICK_ACTIONS = {
        'navigate': (el, e) => { e.preventDefault(); window.showSection?.(el.dataset.section); },
    };

    /**
     * #pdfLibraryUploadModal is static markup in index.html, outside every
     * section, so it binds to document rather than to the library root.
     *
     * It also binds at BOOT rather than on navigation, and that is the load-
     * bearing difference. The upload modal opens from the library screen today,
     * but a document listener created only when someone visits Library is one
     * refactor away from a modal that opens with dead buttons — which is the
     * silent failure delegation trades for, and exactly what happened to the
     * pdf-reader screen when its binding sat behind a feature guard.
     */
    const LIB_OVERLAY_CLICK_ACTIONS = {
        'lib-cancel-upload': () => closeUploadModal(),
        'lib-save-upload': () => submitUpload(),
    };

    let _libBound = false;
    let _libOverlayBound = false;

    function _delegate(root, label, actions) {
        root.addEventListener('click', e => {
            const el = e.target.closest('[data-action]');
            if (!el || !root.contains(el)) return;
            const action = actions[el.dataset.action];
            if (!action) return;
            try {
                action(el, e);
            } catch (err) {
                console.error(`${label} click action "${el.dataset.action}" failed:`, err);
            }
        });
    }

    /** Called from app.js's boot sequence. Overlays exist from first paint. */
    function initOverlayActions() {
        if (_libOverlayBound) return;
        _libOverlayBound = true;
        _delegate(document, '[Library overlay]', LIB_OVERLAY_CLICK_ACTIONS);
    }

    function _bindLibraryActions() {
        if (_libBound) return;
        const root = document.getElementById('library');
        if (!root) return;
        _libBound = true;
        _delegate(root, '[Library]', LIB_CLICK_ACTIONS);
    }

    /**
     * Called on navigation and after any upload or delete. Now async: it loads
     * the partial first, then binds ONCE, then renders. initLibraryUI already
     * renders at the end, so the first call must not render twice.
     */
    async function refresh() {
        if (!await ensureLoaded()) return;

        if (!_uiBound) {
            initLibraryUI();
            _uiBound = true;
            return;
        }

        renderSidebar();
        renderMain();
    }

    function purgeDatabase() {
        return new Promise((resolve) => {
            let settled = false;
            const finish = (ok) => {
                if (settled) return;
                settled = true;
                resolve(ok);
            };
            try {
                localStorage.removeItem(META_KEY);
                if (!('indexedDB' in window)) return finish(true);
                const req = indexedDB.deleteDatabase(DB_NAME);
                req.onsuccess = () => finish(true);
                req.onerror = () => finish(false);
                req.onblocked = () => {};
                setTimeout(() => finish(false), 4000);
            } catch (_) {
                finish(false);
            }
        });
    }

    return {
        getFiles,
        getRecentFiles,
        uploadFile,
        openFile,
        saveLastPage,
        deleteFile,
        getTotalStorageBytes,
        reassignSubject,
        categoryLabel,
        refresh,
        ensureLoaded,
        initLibraryUI,
        initOverlayActions,
        cancelUploadModal: closeUploadModal,
        openInReader: openFileInReader,
        purgeDatabase,
    };
})();

export { PDFLibrary };
export default PDFLibrary;

// Transition shim. flashcards.js uses the bare `PDFLibrary` identifier and
// index.html has inline onclick handlers that need the global. Remove only once
// nothing references it.
if (typeof window !== 'undefined') window.PDFLibrary = PDFLibrary;

console.log('%c📚 PDFLibrary module loaded', 'color: #7c6ff7; font-weight: bold');
